import { supabase } from './supabase';

/**
 * Client for the `nexus-phone-number` edge function — fully in-app phone
 * numbers on platform-owned Twilio subaccounts, billed by Nexus through a
 * prepaid wallet (Twilio-style: balance, auto top-up, hard stop at $0).
 * Separate from the manual Twilio integration (src/lib/twilio.ts), which is
 * left untouched.
 */

export type NexusPhoneStatus = 'active' | 'suspended' | 'released';

export interface NexusPhoneNumber {
  id: string;
  business_id: string;
  twilio_subaccount_sid: string;
  phone_number: string;
  phone_number_sid: string;
  twiml_application_sid: string | null;
  country: string;
  locality: string | null;
  region: string | null;
  status: NexusPhoneStatus;
  suspended_reason: string | null;
  provisioned_at: string;
  suspended_at: string | null;
  released_at: string | null;
}

export interface NexusPhoneBlocker {
  code: string;
  message: string;
}

export interface NexusPhoneWallet {
  balanceCents: number;
  currency: string;
  autoTopup: {
    enabled: boolean;
    thresholdCents: number;
    amountCents: number;
    pausedReason: string | null;
    lastError: string | null;
    lastAttemptAt: string | null;
  };
  totalToppedUpCents: number;
  totalSpentCents: number;
}

export interface NexusPhonePricing {
  markupPercent: number;
  numberMonthlyCents: { CA: number; US: number };
  smsOutboundCents: number;
  smsInboundCents: number;
  mmsOutboundCents: number;
  voiceOutboundPerMinCents: number;
  voiceInboundPerMinCents: number;
  minTopupCents: number;
  maxTopupCents: number;
  minBalanceToProvisionCents: number;
}

export interface NexusPhoneStatusResponse {
  number: NexusPhoneNumber | null;
  wallet: NexusPhoneWallet;
  pricing: NexusPhonePricing;
  card: { brand: string | null; last4: string | null } | null;
  twilio: {
    method: string | null;
    enabled: boolean;
    phoneNumber: string | null;
    callForwardingEnabled: boolean;
    forwardingNumber: string | null;
  };
  billing: { hasSubscription: boolean; subscriptionStatus: string | null };
  canProvision: boolean;
  blockers: NexusPhoneBlocker[];
  configError: string | null;
}

export interface AvailableNexusNumber {
  phoneNumber: string;
  friendlyName: string;
  locality: string | null;
  region: string | null;
  postalCode: string | null;
  country: string;
  capabilities: { sms: boolean; mms: boolean; voice: boolean };
}

export interface NexusPhoneTransaction {
  id: string;
  type: 'topup' | 'usage' | 'adjustment' | 'refund';
  amount_cents: number;
  balance_after_cents: number;
  description: string | null;
  created_at: string;
}

export class NexusPhoneError extends Error {
  code: string | null;
  status: number | null;
  blockers: NexusPhoneBlocker[];
  constructor(message: string, opts: { code?: string | null; status?: number | null; blockers?: NexusPhoneBlocker[] } = {}) {
    super(message);
    this.name = 'NexusPhoneError';
    this.code = opts.code ?? null;
    this.status = opts.status ?? null;
    this.blockers = opts.blockers ?? [];
  }
}

// supabase.functions.invoke hides non-2xx bodies behind error.context — unwrap
// so the UI can show the real reason (and the structured code).
async function invoke<T>(action: string, body: Record<string, unknown>): Promise<T> {
  if (!supabase) throw new NexusPhoneError('App not initialized');
  const { data, error } = await supabase.functions.invoke('nexus-phone-number', {
    body: { action, ...body },
  });
  if (error) {
    let detail: { error?: string; code?: string; blockers?: NexusPhoneBlocker[] } | null = null;
    let status: number | null = null;
    try {
      const ctx = (error as { context?: Response }).context;
      status = ctx?.status ?? null;
      detail = (await ctx?.json()) ?? null;
    } catch {
      /* keep generic */
    }
    throw new NexusPhoneError(detail?.error || error.message || 'Request failed', {
      code: detail?.code ?? null,
      status,
      blockers: detail?.blockers ?? [],
    });
  }
  if (data && typeof data.error === 'string') {
    throw new NexusPhoneError(data.error, { code: data.code ?? null, blockers: data.blockers ?? [] });
  }
  return data as T;
}

export function getNexusPhoneStatus(businessId: string): Promise<NexusPhoneStatusResponse> {
  return invoke<NexusPhoneStatusResponse>('status', { businessId });
}

/** How the server found the returned numbers — most specific filter that had inventory. */
export type NexusSearchMatchedBy = 'city' | 'near' | 'region' | 'areaCode' | 'any';

export function searchNexusNumbers(params: {
  businessId: string;
  country?: 'CA' | 'US';
  /** City name (matched against Twilio's locality / rate center). */
  city?: string;
  /** Province/state code, e.g. "BC". */
  region?: string;
  /** City coordinates — enables the "nearest available" fallback. */
  lat?: number;
  lng?: number;
  areaCode?: string;
  contains?: string;
}): Promise<{ numbers: AvailableNexusNumber[]; country: string; matchedBy: NexusSearchMatchedBy }> {
  return invoke('search', params);
}

export type AddFundsResult =
  | { success: true; charged: true; amountCents: number; wallet: NexusPhoneWallet; reactivated: boolean }
  | { success: true; charged: false; requiresCheckout: true; checkoutUrl: string; sessionId: string; reason: string; reasonMessage: string };

export function addNexusPhoneFunds(params: {
  businessId: string;
  amountCents: number;
  source?: 'manual' | 'initial';
  successUrl?: string;
  cancelUrl?: string;
}): Promise<AddFundsResult> {
  return invoke('add_funds', params);
}

export function confirmNexusPhoneCheckout(params: {
  businessId: string;
  sessionId: string;
}): Promise<{ success: true; alreadyProcessed: boolean; wallet: NexusPhoneWallet; reactivated: boolean }> {
  return invoke('confirm_checkout', params);
}

export function setNexusPhoneAutoTopup(params: {
  businessId: string;
  enabled: boolean;
  thresholdCents?: number;
  amountCents?: number;
}): Promise<{ success: true; wallet: NexusPhoneWallet }> {
  return invoke('set_auto_topup', params);
}

export function provisionNexusNumber(params: {
  businessId: string;
  phoneNumber: string;
  country?: string;
  locality?: string | null;
  region?: string | null;
}): Promise<{ success: true; number: NexusPhoneNumber; wallet: NexusPhoneWallet }> {
  return invoke('provision', params);
}

export function releaseNexusNumber(businessId: string): Promise<{ success: true; warnings: string[]; wallet: NexusPhoneWallet }> {
  return invoke('release', { businessId });
}

/** Cheap read used by the Integrations card (RLS lets members see their row). */
export async function fetchLiveNexusNumber(businessId: string): Promise<NexusPhoneNumber | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('business_nexus_phone_numbers')
    .select('*')
    .eq('business_id', businessId)
    .neq('status', 'released')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    // Table not migrated yet, or no access — treat as "no number".
    console.warn('fetchLiveNexusNumber:', error.message);
    return null;
  }
  return (data as NexusPhoneNumber | null) ?? null;
}

/** Recent wallet activity (RLS-scoped). */
export async function fetchNexusPhoneTransactions(businessId: string, limit = 10): Promise<NexusPhoneTransaction[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('nexus_phone_wallet_transactions')
    .select('id, type, amount_cents, balance_after_cents, description, created_at')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) {
    console.warn('fetchNexusPhoneTransactions:', error.message);
    return [];
  }
  return (data as NexusPhoneTransaction[]) ?? [];
}

/** Whole-cent amounts (balances, top-ups). */
export function formatMoney(cents: number | null | undefined, currency: string | null | undefined = 'usd'): string {
  if (cents == null) return '—';
  const cur = (currency || 'usd').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, minimumFractionDigits: cents % 100 === 0 ? 0 : 2 }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)} ${cur}`;
  }
}

/** Sub-cent unit prices (per text, per minute) → "1.04¢" / "$0.0104". */
export function formatUnitPrice(cents: number): string {
  if (cents >= 100) return `$${(cents / 100).toFixed(2)}`;
  const rounded = Math.round(cents * 100) / 100;
  return `${rounded.toFixed(rounded % 1 === 0 ? 0 : 2)}¢`;
}

/** +16045551234 → (604) 555-1234 for NANP numbers; other numbers unchanged. */
export function formatNexusPhone(e164: string | null | undefined): string {
  if (!e164) return '';
  const digits = e164.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return e164;
}
